import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../../../config/firebaseConfig";
import {
  getDoc,
  updateDoc,
  getDocs,
  collection,
  query,
  where
} from "firebase/firestore";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ja } from "date-fns/locale";
import Holidays from "date-holidays";
import "./tab02.css";
import ReportModal from "./ReportModal/ReportModal";

// 日本語ロケール登録
registerLocale("ja", ja);

// 祝日判定インスタンス
const hd = new Holidays("JP");

// 日付セルに土日祝のクラスを付与
const dayClassName = (date) => {
  if (hd.isHoliday(date)) return "holiday";
  const day = date.getDay();
  return day === 0 ? "sunday" : day === 6 ? "saturday" : undefined;
};

const Tab02 = ({ canEdit, isEditing, setIsEditing, onEditModeChange }) => {
  // 1つ目のテーブルの表示する日付項目と色
  const scheduleFields = [
    { key: "DesiredCompletionDate", label: "工事完了希望日", color: "var(--color-orange)" },
    { key: "PlannedStartDate", label: "工事予定日", color: "var(--color-purple)" },
    { key: "EndDate", label: "実績終了日", color: "var(--color-purple)" }
  ];

  // 2つ目の表の表示したい行要素
  const otherTasks = [
    { label: "現地確認", field: "OnsiteConfirmationDate", monthsShown: 6, selectsRange: true, placeholder: "開始〜終了", color: "var(--color-others)" },
    { label: "搬入日", field: "deliveryDate", monthsShown: 6, selectsRange: true, placeholder: "開始〜終了", color: "var(--color-others)" },
  ];

  // カテゴリ設定
  const categories = {
    electric: { label: "電気", color: "var(--color-electric)" },
    network: { label: "ネットワーク", color: "var(--color-network)" },
    air: { label: "給排気", color: "var(--color-air)" },
    water: { label: "給排水", color: "var(--color-water)" },
  };

  const { masterID } = useParams();
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState({});
  const [initialSchedule, setInitialSchedule] = useState({});
  const [loading, setLoading] = useState(true);
  const [isModified, setIsModified] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [reportData, setReportData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ranges, setRanges] = useState({});
  const currentProjectID = localStorage.getItem("currentProjectID");
  

  const handleSubmit = () => {
    // 送信処理
    alert("終了報告が送信されました！");
    // setIsModalOpen(false);
    setSelectedField(null) // ✅ モーダルを閉じる
};
  // 日付を yyyy-MM-dd 形式の JST に変換
const formatDateToYMD = (date) => {
  if (!date) return null;

  const parsedDate = new Date(date); // ← 強制的にDate化
  if (isNaN(parsedDate)) return null; // 無効な日付は除外

  const jst = new Date(parsedDate.getTime() + 9 * 60 * 60 * 1000);
  return jst.toISOString().split("T")[0];
};


  // Firestore からデータ取得
  useEffect(() => {
  const fetchSchedule = async () => {
    const q = query(
      collection(db, "00_DeviceMasterTable"),
      where("masterID", "==", masterID),
      where("projectID", "==", currentProjectID)
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      const data = snap.docs[0].data().schedule || {};
      setInitialSchedule(structuredClone(data));
      setSchedule(structuredClone(data));

      // ▼ reportData を生成
      const newReportData = {};
      Object.entries(data).forEach(([catKey, category]) => {
        if (!category) return;

        if (catKey === "others") {
          // 3階層目
          Object.entries(category).forEach(([key, value]) => {
            if (value?.EndDate) {
              if (!newReportData[catKey]) newReportData[catKey] = {};
              newReportData[catKey][key] = value.EndDate;
            }
          });
        } else {
          // 2階層目
          if (category?.EndDate) {
            if (!newReportData[catKey]) newReportData[catKey] = {};
            newReportData[catKey]["EndDate"] = category.EndDate;
          }
        }
      });

      setReportData(newReportData);  // ← ここでセット
    }
    setLoading(false);
  };
  fetchSchedule();
}, [masterID]);


  // 初期値もセット
  useEffect(() => {
    const initialRanges = {};
    Object.entries(schedule).forEach(([catKey, value]) => {
      if (!value) return;
      if (["electric", "air", "water", "network"].includes(catKey)) {
        if (Array.isArray(value.PlannedStartDate)) {
          initialRanges[`${catKey}-PlannedStartDate`] = value.PlannedStartDate.map((d) => d ? new Date(d) : null);
        }
      }
      if (catKey === "others") {
        Object.entries(value).forEach(([field, val]) => {
          if (Array.isArray(val?.workingDate)) {
            initialRanges[`others-${field}`] = val.workingDate.map((d) => d ? new Date(d) : null);
          }
        });
      }
    });
    setRanges(initialRanges);
  }, [schedule]);


  // 差分チェック
  useEffect(() => {
    setIsModified(JSON.stringify(schedule) !== JSON.stringify(initialSchedule));
  }, [schedule, initialSchedule]);

  // 未保存時の離脱警告
  useEffect(() => {
    const handler = (e) => {
      if (isModified) {
        e.returnValue = "未保存の変更があります。";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isModified]);


    const handleChange = (catKey, field, value, isRange = false) => {
  setSchedule((prev) => {
    const updated = { ...prev };
    const isInfra = ["water", "electric", "air", "network"].includes(catKey);

    if (!updated[catKey]) updated[catKey] = {};

    if (isRange) {
      // 日付範囲 (開始〜終了)
      const formatted = value.map((d) => (d ? formatDateToYMD(d) : null));

      if (isInfra) {
        // infrastructure 系はそのまま保存
        updated[catKey][field] = formatted;
      } else {
        // others → workingDate として保存
        if (!updated[catKey][field]) updated[catKey][field] = {};
        updated[catKey][field].workingDate = formatted;
      }
    } else {
      // 単一日付 → EndDate.reportDate に保存
      const formatted = value ? formatDateToYMD(value) : null;

      if (isInfra) {
        // infrastructure 系はそのまま保存
        updated[catKey][field] = formatted;
      } else {
        if (!updated[catKey][field]) updated[catKey][field] = {};
        if (!updated[catKey][field].EndDate) updated[catKey][field].EndDate = {};
        updated[catKey][field].EndDate.reportDate = formatted;
      }
    }

    return updated;
  });
};



function isValidDate(d) {
  return d instanceof Date && !isNaN(d);
}


  // 保存処理
const handleSave = async () => {
  const q = query(
    collection(db, "00_DeviceMasterTable"),
    where("masterID", "==", masterID),
    where("projectID", "==", currentProjectID)
  );
  const snap = await getDocs(q);
  if (snap.empty) return alert("該当データがありません");

  try {
    const mergedSchedule = structuredClone(schedule);

    // 🔧 空文字を null に変換する関数
    const trimOrNull = (val) => {
      if (typeof val !== "string") return null;
      const trimmed = val.trim();
      return trimmed === "" ? null : trimmed;
    };

    Object.entries(reportData).forEach(([catKey, value]) => {
      Object.entries(value).forEach(([key, report]) => {
        const normalizedReport = {
          reportDate: report?.reportDate ?? null,
          reporter: trimOrNull(report?.reporter),
          remarks: trimOrNull(report?.remarks),
        };

        if (["water", "electric", "air", "network"].includes(catKey)) {
          if (!mergedSchedule[catKey]) mergedSchedule[catKey] = {};
          mergedSchedule[catKey]["EndDate"] = normalizedReport;
        } else if (catKey === "others") {
          if (!mergedSchedule.others) mergedSchedule.others = {};
          if (!mergedSchedule.others[key]) mergedSchedule.others[key] = {};
          mergedSchedule.others[key]["EndDate"] = normalizedReport;
        }
      });
    });

    // 🔍 デバッグログで確認
    console.log("🚀 mergedSchedule to be saved:", JSON.stringify(mergedSchedule, null, 2));

    await updateDoc(snap.docs[0].ref, {
      schedule: mergedSchedule,
    });

    setInitialSchedule(structuredClone(mergedSchedule));
    setIsEditing(false);
    alert("更新しました");
  } catch (err) {
    console.error("🔥 保存時のエラー:", err);
    alert("保存時にエラーが発生しました");
  }
};




  // キャンセル処理
  const handleCancel = () => {
    const confirmCancel = window.confirm("変更内容が登録されません。編集作業を中止しますか？");
    if (!confirmCancel) return;
    masterID ? setSchedule(structuredClone(initialSchedule)) : navigate("/gantt");
    setIsEditing(false);
  };

  if (loading) return <p>データ取得中...</p>;

  

//――――――――――――――――――――――――――――― 
// html
// ―――――――――――――――――――――――――――――

  return (
    <div className="schedule-container">
      <h2>スケジュール設定</h2>
      <div className="header">
        {isEditing ? (
          <div className="button-group">
            <button onClick={handleSave} className="MasterFormSubmit">{masterID ? "更新" : "新規登録"}</button>
            <button onClick={handleCancel} className="MasterFormCancel">キャンセル</button>
          </div>
        ) : (
          <button onClick={() => setIsEditing(true)} className="edit-button">編集</button>
        )}
      </div>

      
  {/* 1つ目のテーブル */}
<h3>装置メーカ・搬入会社　原動工事会社入力</h3>
<table className="schedule-table">
  <thead>
    <tr>
      <th>カテゴリ</th>
      {scheduleFields.map(({ key, label, color }) => (
        <th key={key} style={{ backgroundColor: color }}>{label}</th>
      ))}
    </tr>
  </thead>
  <tbody>
    {Object.entries(categories).map(([catKey, { label, color }]) => (
      canEdit[catKey] && (
        <tr key={catKey}>
          <td style={{ backgroundColor: color }}>{label}</td>
          {scheduleFields.map(({ key }) => {
            const field = key;
            const rangeKey = `${catKey}-${field}`;
            const currentRange = ranges[rangeKey] || [null, null];

            return (
              <td key={`${catKey}-${key}`}>
                {/* 工事予定日（範囲選択） */}
                {key === "PlannedStartDate" ? (
                  <DatePicker
                    locale="ja"
                    dateFormat="yyyy-MM-dd"
                    monthsShown={6}
                    dayClassName={dayClassName}
                    calendarClassName="small-datepicker"
                    disabled={!isEditing}
                    selectsRange
                    startDate={currentRange[0]}
                    endDate={currentRange[1]}
                    onChange={(range) => {
                      setRanges((prev) => ({ ...prev, [rangeKey]: range }));
                      handleChange(catKey, field, range, true);
                    }}
                    placeholderText="開始〜終了"
                    isClearable
                    withPortal
                  />
                ) : key === "EndDate" ? (
                  <>
                    <button
                      disabled={!isEditing}
                      onClick={() => {
                        setSelectedField({ category: catKey, key });
                        setIsModalOpen(true);
                      }}
                      className="rounded report-btn"
                    >
                      {reportData[catKey]?.[key]?.reportDate ? "編集する" : "終了報告をする"}
                    </button>
                    {reportData[catKey]?.[key] &&
                      (reportData[catKey][key].reportDate ||
                      reportData[catKey][key].reporter ||
                      reportData[catKey][key].remarks) && (
                        <div className="report-preview" style={{ marginTop: "8px" }}>
                          <p><strong>完了日：</strong>{reportData[catKey][key].reportDate}</p>
                          <p><strong>入力者：</strong>{reportData[catKey][key].reporter}</p>
                          <p><strong>備考：</strong>{reportData[catKey][key].remarks}</p>
                        </div>
                    )}

                  </>
                ) : (
                  // 工事完了希望日など単日入力
                  <DatePicker
                    locale="ja"
                    dateFormat="yyyy-MM-dd"
                    monthsShown={6}
                    dayClassName={dayClassName}
                    calendarClassName="small-datepicker"
                    disabled={!isEditing}
                    selected={schedule[catKey]?.[key] ? new Date(schedule[catKey][key]) : null}
                    onChange={(date) => handleChange(catKey, key, date)}
                    placeholderText="選択"
                    isClearable
                    withPortal
                  />
                )}
              </td>
            );
          })}
        </tr>
      )
    ))}
  </tbody>
</table>

    {/* 2つ目のテーブル */}
<h3>装置メーカ・搬入会社のみ入力</h3>
<table className="schedule-table manufacturing">
  <thead>
    <tr>
      <th>作業内容</th>
      <th>作業日</th>
      <th>終了日</th>
    </tr>
  </thead>
  <tbody>
    {otherTasks.map(({ label, field, monthsShown, selectsRange, placeholder, color }) => {
      const rangeKey = `others-${field}`;
      const currentRange = ranges[rangeKey] || [null, null];
      return (
        <tr key={field} className="others checkDate">
          <td className="label-highlight" style={{ backgroundColor: color }}>{label}</td>
          <td>
            <DatePicker
              locale="ja"
              dateFormat="yyyy-MM-dd"
              monthsShown={monthsShown}
              dayClassName={dayClassName}
              selectsRange={selectsRange}
              disabled={!isEditing}
              calendarClassName="small-datepicker"
              startDate={currentRange[0]}
              endDate={currentRange[1]}
              onChange={(val) => {
                setRanges((prev) => ({ ...prev, [rangeKey]: val }));
                handleChange("others", field, val, true);
              }}
              placeholderText={placeholder}
              isClearable
              withPortal
              popperModifiers={[
                {
                  name: "preventOverflow",
                  options: { boundary: "viewport" },
                },
                {
                  name: "flip",
                  options: {
                    fallbackPlacements: ["bottom-start", "top-start", "bottom-end", "top-end"],
                  },
                },
              ]}
            />
          </td>
          <td>
            <button
              disabled={!isEditing}
              onClick={() => {
                setSelectedField({ category: "others", key: field });
                setIsModalOpen(true);
              }}
              className="rounded"
            >
              {reportData["others"]?.[field]?.reportDate ? "編集する" : "終了報告をする"}
            </button>
              {(() => {
                const report = reportData["others"]?.[field];
                const hasAnyValue = report?.reportDate || report?.reporter || report?.remarks;

                return hasAnyValue ? (
                  <div className="report-preview">
                    <p><strong>完了日：</strong>{report.reportDate}</p>
                    <p><strong>入力者：</strong>{report.reporter}</p>
                    <p><strong>備考：</strong>{report.remarks}</p>
                  </div>
                ) : null;
              })()}
          </td>
        </tr>
      );
    })}
  </tbody>
</table>
      
     {/* ✅ モーダル呼び出し（共通） */}
{isModalOpen && selectedField && (
  <ReportModal
  isOpen={isModalOpen}
  onClose={() => {
    setIsModalOpen(false);
    setSelectedField(null);
  }}
  onSubmit={(data) => {
    const { category, key } = selectedField;
    setReportData((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: data,
      },
    }));
    setIsModalOpen(false);
    setSelectedField(null);
  }}
  onDelete={() => {
    const { category, key } = selectedField;
    setReportData((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: null, // ✅ nullとして保持し、handleSaveでEndDate: nullに保存
      },
    }));
    setIsModalOpen(false);
    setSelectedField(null);
  }}
  initialData={reportData[selectedField.category]?.[selectedField.key] || null}
/>

)}
    </div>
  );
};

export default Tab02;